import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, { notificationsAPI } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/UserNotifications.css';

const UserNotifications = () => {
  const { t, i18n } = useTranslation();
  const { setUnreadNotifications } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [translations, setTranslations] = useState({});
  const [translatingId, setTranslatingId] = useState(null);

  const handleTranslate = async (e, notif) => {
    e.stopPropagation();
    if (translations[notif.id]) {
      setTranslations(prev => { const copy = { ...prev }; delete copy[notif.id]; return copy; });
      return;
    }
    const lang = i18n.language === 'fr' ? 'en' : i18n.language;
    setTranslatingId(notif.id);
    try {
      const text = notif.title + '\n---\n' + notif.content;
      const res = await api.post('/translate', { text, targetLang: lang });
      const parts = res.data.translatedText.split('\n---\n');
      setTranslations(prev => ({ ...prev, [notif.id]: { title: parts[0], content: parts[1] || '' } }));
    } catch (err) {
      console.error('Erreur traduction:', err);
    } finally {
      setTranslatingId(null);
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getMy();
      setNotifications(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClick = async (notif) => {
    setExpandedId(expandedId === notif.id ? null : notif.id);

    if (!notif.is_read) {
      try {
        await notificationsAPI.markAsRead(notif.id);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n)
        );
        // Mettre à jour le compteur dans le header
        const countRes = await notificationsAPI.getUnreadCount();
        setUnreadNotifications(countRes.data.count);
      } catch (err) {
        // silently fail
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadNotifications(0);
    } catch (err) {
      // silently fail
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="user-notifications">
      <div className="notif-page-header">
        <h1>{t('notifications.title')}</h1>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className="mark-all-btn">
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="notif-empty">
          <span className="notif-empty-icon">🔔</span>
          <p>{t('notifications.empty')}</p>
        </div>
      ) : (
        <div className="notif-list">
          {notifications.map(notif => (
            <div
              key={notif.id}
              className={`notif-card ${!notif.is_read ? 'unread' : ''} ${expandedId === notif.id ? 'expanded' : ''}`}
              onClick={() => handleClick(notif)}
            >
              <div className="notif-card-header">
                <div className="notif-card-left">
                  {!notif.is_read && <span className="unread-dot"></span>}
                  <div>
                    <h3 className="notif-card-title">{notif.title}</h3>
                    <span className="notif-card-meta">
                      {notif.type === 'broadcast' ? t('notifications.general') : t('notifications.personal')}
                      {' — '}
                      {formatDateTime(notif.created_at)}
                    </span>
                  </div>
                </div>
                <span className={`notif-chevron ${expandedId === notif.id ? 'open' : ''}`}>
                  &#9660;
                </span>
              </div>

              {expandedId === notif.id && (
                <div className="notif-card-body">
                  <p>{notif.content}</p>
                  {translations[notif.id] && (
                    <div className="notif-translated">
                      <strong>{translations[notif.id].title}</strong>
                      <p>{translations[notif.id].content}</p>
                    </div>
                  )}
                  <button
                    className={`btn-translate-notif ${translations[notif.id] ? 'active' : ''}`}
                    onClick={(e) => handleTranslate(e, notif)}
                    disabled={translatingId === notif.id}
                  >
                    {translatingId === notif.id ? '⏳' : '🌐'} {translations[notif.id] ? t('product.showOriginal') : t('product.translate')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserNotifications;
